import React from 'react'
import {Box, Typography} from '@mui/material'
import Card from 'src/components-new/layout/Card'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import EmptyState from 'src/components-new/layout/EmptyState'
import {NEUTRAL_SCALE, CARD_BORDER_COLOR, CARD_BORDER_RADIUS} from 'src/theme-new/tokens'

const SEX_OPTIONS = [
  {value: 'MALE', label: 'Maschio'},
  {value: 'FEMALE', label: 'Femmina'},
  {value: 'NOT_DECLARED', label: 'Non dichiarato'},
]

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('it-IT', {day: 'numeric', month: 'long', year: 'numeric'})
}

function optionLabel(value: string, options: {value: string; label: string}[]): string | null {
  return options.find(o => o.value === value)?.label ?? null
}

function TwoColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
        gap: 2,
        mb: 1,
      }}
    >
      {children}
    </Box>
  )
}

function ThreeColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 2}}>
      {children}
    </Box>
  )
}

function SectionLabel({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block'}}>
      {children}
    </Typography>
  )
}

function InfoRow({label, value}: {label: string; value?: string | null}): JSX.Element {
  return (
    <Box sx={{display: 'flex', gap: 1, alignItems: 'baseline'}}>
      <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500], minWidth: 80, flexShrink: 0}}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{color: value ? 'text.primary' : NEUTRAL_SCALE[300], wordBreak: 'break-word'}}>
        {value || '—'}
      </Typography>
    </Box>
  )
}

interface UserDataSectionProps {
  values: Record<string, any>
  setField: (key: string, value: any) => void
  fields: Record<string, any>
  data: Record<string, any>
  qualificationsById?: Record<string, string>
}

export default function UserDataSection({values, setField, fields, data, qualificationsById = {}}: UserDataSectionProps): JSX.Element {
  return (
    <Card title="Dati anagrafici">
      <TwoColGrid>
        {!fields?.title?.hidden && (
          fields?.title?.readOnly
            ? <ReadOnlyField label="Titolo" value={values.title} fullWidth />
            : (
              <TextFieldNew
                label="Titolo"
                value={values.title ?? ''}
                onChange={v => setField('title', v)}
                fullWidth
              />
            )
        )}
        {!fields?.lastName?.hidden && (
          fields?.lastName?.readOnly
            ? <ReadOnlyField label="Cognome" value={values.lastName} fullWidth />
            : (
              <TextFieldNew
                label="Cognome"
                value={values.lastName ?? ''}
                onChange={v => setField('lastName', v)}
                required
                fullWidth
              />
            )
        )}
        {!fields?.firstName?.hidden && (
          fields?.firstName?.readOnly
            ? <ReadOnlyField label="Nome" value={values.firstName} fullWidth />
            : (
              <TextFieldNew
                label="Nome"
                value={values.firstName ?? ''}
                onChange={v => setField('firstName', v)}
                required
                fullWidth
              />
            )
        )}
        {!fields?.code?.hidden && (
          fields?.code?.readOnly
            ? <ReadOnlyField label="Codice" value={values.code} fullWidth />
            : (
              <TextFieldNew
                label="Codice"
                value={values.code ?? ''}
                onChange={v => setField('code', v)}
                fullWidth
              />
            )
        )}
        {!fields?.sex?.hidden && (
          fields?.sex?.readOnly
            ? <ReadOnlyField label="Sesso" value={optionLabel(values.sex, SEX_OPTIONS)} fullWidth />
            : (
              <SelectNew
                label="Sesso"
                value={values.sex ?? ''}
                onChange={v => setField('sex', v)}
                options={SEX_OPTIONS}
                fullWidth
              />
            )
        )}
        {!fields?.nationality?.hidden && (
          fields?.nationality?.readOnly
            ? <ReadOnlyField label="Nazionalità" value={values.nationality} fullWidth />
            : (
              <TextFieldNew
                label="Nazionalità"
                value={values.nationality ?? ''}
                onChange={v => setField('nationality', v)}
                fullWidth
              />
            )
        )}
        {!fields?.birthDate?.hidden && (
          fields?.birthDate?.readOnly
            ? <ReadOnlyField label="Data di Nascita" value={formatDate(values.birthDate)} fullWidth />
            : (
              <DatePickerNew
                label="Data di Nascita"
                value={values.birthDate ?? null}
                onChange={v => setField('birthDate', v)}
                fullWidth
              />
            )
        )}
        {fields?.fiscalCode && !fields?.fiscalCode?.hidden && (
          fields?.fiscalCode?.readOnly
            ? <ReadOnlyField label="Codice fiscale" value={values.fiscalCode} fullWidth />
            : (
              <TextFieldNew
                label="Codice fiscale"
                value={values.fiscalCode ?? ''}
                onChange={v => setField('fiscalCode', v)}
                fullWidth
              />
            )
        )}
        {!fields?.createdAt?.hidden && (
          <ReadOnlyField
            label="Data di inserimento"
            value={formatDate(values.createdAt)}
            fullWidth
          />
        )}
        {!fields?.specialization?.hidden && (
          fields?.specialization?.readOnly
            ? <ReadOnlyField label="Specializzazione" value={values.specialization} fullWidth />
            : (
              <TextFieldNew
                label="Specializzazione"
                value={values.specialization ?? ''}
                onChange={v => setField('specialization', v)}
                fullWidth
              />
            )
        )}
      </TwoColGrid>

      {!fields?.prescribingDoctorId?.hidden && (
        <Box sx={{mb: 2}}>
          <ReadOnlyField label="Medico prescrittore" value={data?.prescribingDoctor} fullWidth />
        </Box>
      )}

      {!fields?.medicalCenterId?.hidden && (
        <Box sx={{mb: 3}}>
          <SectionLabel>{'Centri Medici'}</SectionLabel>
          {(data?.medicalCenters ?? []).length === 0 ? (
            <EmptyState title="Nessun risultato" description="Nessun centro medico registrato" />
          ) : (
            <ThreeColGrid>
              {(data?.medicalCenters ?? []).map((center: any) => {
                const addressLine = [
                  [center.address, center.streetNumber].filter(Boolean).join(' '),
                  center.postalCode,
                  center.city,
                  center.province,
                ].filter(Boolean).join(', ')
                return (
                  <Box
                    key={center._id}
                    sx={{
                      border: `1px solid ${CARD_BORDER_COLOR}`,
                      borderRadius: CARD_BORDER_RADIUS,
                      p: 2,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 0.25}}>
                      {center.name || '—'}
                    </Typography>
                    <InfoRow label="Indirizzo" value={addressLine || undefined} />
                    <InfoRow label="Telefono" value={center.phoneNumber} />
                    <InfoRow label="Email" value={center.email} />
                    <InfoRow label="Sito web" value={center.website} />
                  </Box>
                )
              })}
            </ThreeColGrid>
          )}
        </Box>
      )}

      {!fields?.operatorsIds?.hidden && (
        <Box sx={{mb: 2}}>
          <SectionLabel>{'Operatori sanitari'}</SectionLabel>
          {(data?.operators ?? []).length === 0 ? (
            <ThreeColGrid>
              <Box
                sx={{
                  border: `1px solid ${CARD_BORDER_COLOR}`,
                  borderRadius: CARD_BORDER_RADIUS,
                  p: 2,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 84,
                }}
              >
                <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500], textAlign: 'center'}}>
                  {'Nessun operatore sanitario registrato'}
                </Typography>
              </Box>
            </ThreeColGrid>
          ) : (
            <ThreeColGrid>
              {(data?.operators ?? []).map((operator: any) => {
                const name = `${operator.firstName ?? ''} ${operator.lastName ?? ''}`.trim()
                const quals = (operator.qualifications ?? [])
                  .map((q: any) => (typeof q === 'string' ? (qualificationsById[q] ?? null) : (q?.name ?? null)))
                  .filter(Boolean)
                  .join(', ')
                return (
                  <Box
                    key={operator._id}
                    sx={{
                      border: `1px solid ${CARD_BORDER_COLOR}`,
                      borderRadius: CARD_BORDER_RADIUS,
                      p: 2,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 0.25}}>
                      {name || '—'}
                    </Typography>
                    <InfoRow label="Qualifiche" value={quals || null} />
                    <InfoRow label="Telefono" value={operator.cellPhoneNumber ?? operator.phoneNumber} />
                    <InfoRow label="Email" value={operator.email} />
                  </Box>
                )
              })}
            </ThreeColGrid>
          )}
        </Box>
      )}

    </Card>
  )
}
